import { FC, useEffect, useState } from 'react';

import { floorWon, formatWon, getSettlementPayoutDate } from '../../utils/settlement.utils';

// ─────────────────────────────────────────────
// SettlementCreateModal
//
// 미정산 내역에서 선택한 배송들을 한 정산 문서(Draft)로 묶어 생성(집행)하는 모달.
// 업체정산(supply)·상조정산(rebate) 두 섹션이 공유한다.
// ─────────────────────────────────────────────

export interface SettlementCreateModalProps {
  open: boolean;
  targetName: string;
  count: number;
  /** 선택 배송 계산 합계(반올림 전) */
  amount: number;
  onClose: () => void;
  onConfirm: (args: {
    year: number;
    month: number;
    memo: string;
    /** 실제 정산액(신규 정산 생성 시 계산값과 다르면 동결) */
    overrideAmount: number;
  }) => Promise<void>;
}

export const SettlementCreateModal: FC<SettlementCreateModalProps> = ({
  open,
  targetName,
  count,
  amount,
  onClose,
  onConfirm,
}) => {
  // 계산값 — 실제 정산액 입력의 기본값/참조값 (서버 계산이 정수라 floorWon은 구 데이터 가드)
  const computed = floorWon(amount);
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [month, setMonth] = useState(() => new Date().getMonth() + 1);
  const [paid, setPaid] = useState<number>(computed);
  const [memo, setMemo] = useState('');
  const [loading, setLoading] = useState(false);

  // 모달은 항상 트리에 남아있어 재오픈 시 값이 초기화되지 않으므로, 열릴 때 오늘/계산금액으로 리셋한다.
  useEffect(() => {
    if (open) {
      setYear(new Date().getFullYear());
      setMonth(new Date().getMonth() + 1);
      setPaid(floorWon(amount));
      setMemo('');
    }
  }, [open, amount]);

  if (!open) return null;

  const diff = paid - computed;
  // 정산예정일: 정산월 다음달 15일 (공용 도출 규칙)
  const payoutDate = getSettlementPayoutDate(year, month);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm({ year, month, memo, overrideAmount: paid });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <h3 className="mb-4 text-base font-bold text-gray-800">정산 집행</h3>
        <div className="mb-4 space-y-2 text-sm text-gray-600">
          <div>
            <span className="font-medium">대상:</span> {targetName}
          </div>
          <div>
            <span className="font-medium">배송 건수:</span> {count}건
          </div>
          <div>
            <span className="font-medium">계산 금액:</span> {formatWon(computed)}
          </div>
        </div>

        {/* 정산월 */}
        <div className="mb-3 flex items-center gap-2 text-sm">
          <span className="font-medium text-gray-700">정산월</span>
          <input
            type="number"
            value={year}
            min={2020}
            max={2099}
            onChange={(e) => setYear(Number(e.target.value))}
            className="w-20 rounded border border-gray-300 px-2 py-1 text-center"
          />
          <span>년</span>
          <input
            type="number"
            value={month}
            min={1}
            max={12}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="w-14 rounded border border-gray-300 px-2 py-1 text-center"
          />
          <span>월</span>
        </div>

        {/* 정산예정일 — 정산월 기준 자동 (저장하지 않는 안내값) */}
        <div className="mb-3 flex items-center gap-2 text-sm text-gray-600">
          <span className="font-medium text-gray-700">정산예정일</span>
          <span className="tabular-nums">
            {payoutDate.getFullYear()}년 {payoutDate.getMonth() + 1}월 {payoutDate.getDate()}일
          </span>
          <span className="text-xs text-gray-400">(정산월 기준 자동)</span>
        </div>

        {/* 실제 정산액 */}
        <div className="mb-1 flex items-center gap-2 text-sm">
          <span className="font-medium text-gray-700">실제 정산액</span>
          <input
            type="number"
            value={paid}
            min={0}
            step={1}
            onChange={(e) => setPaid(Math.max(0, Math.floor(Number(e.target.value) || 0)))}
            className="w-36 rounded border border-gray-300 px-2 py-1 text-right tabular-nums"
          />
          <span>원</span>
        </div>
        {diff !== 0 ? (
          <p className="mb-3 text-xs text-amber-600">
            계산 금액과 {formatWon(Math.abs(diff))} {diff > 0 ? '많음' : '적음'} — 신규 정산은 이 금액으로 동결됩니다.
          </p>
        ) : (
          <p className="mb-3 text-xs text-gray-400">
            기존 월 정산에 합쳐지는 경우엔 입력한 금액 대신 자동 재계산됩니다.
          </p>
        )}

        {/* 메모 */}
        <div className="mb-5">
          <label className="mb-1 block text-sm font-medium text-gray-700">메모</label>
          <textarea
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            rows={2}
            placeholder="정산 관련 메모 (선택)"
            className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </div>

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={loading}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            취소
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading ? '집행 중...' : '집행 확인'}
          </button>
        </div>
      </div>
    </div>
  );
};
